import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { DetailSection } from '../../TripDetailPanel.styles';
import { CustomerDetailsSectionProps,Customer } from '../../../../types/customerDetails';
import { sectionBoxSx,headerSx,
  headerLeftSx,
  avatarSx,
  avatarTextSx,
  headerTitleSx,
  modifyButtonSx,
  contentBoxSx,
  gridSx,
  labelSx,
  valueSx,
  textFieldSx,
  selectSx,
  addressFieldSx,
  editActionsSx,
  cancelButtonSx,
  saveButtonSx } from '../../styles/CustomerDetails.styles';

const CustomerDetailsSection: React.FC<CustomerDetailsSectionProps> = ({
  customer,
  onUpdate,
  loading = false,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    address: '',
    gender: '',
    emergencyContactName: '',
    emergencyContact: ''
  });


  // Helper function to get the first available value
  const getFieldValue = (...values: (string | undefined | null)[]): string => {
    for (const value of values) {
      if (value && value.trim() !== '') {
        return value;
      }
    }
    return 'N/A';
  };

  const handleEdit = () => {
    setEditData({
      name: getFieldValue(customer?.name, customer?.customerName, customer?.fullName),
      email: getFieldValue(customer?.email, customer?.emailId, customer?.emailAddress),
      address: getFieldValue(customer?.address, customer?.customerAddress, customer?.fullAddress),
      gender: getFieldValue(customer?.gender),
      emergencyContactName: getFieldValue(customer?.emergencyContactName, customer?.emergencyContactPerson, customer?.emergencyContactPersonName),
      emergencyContact: getFieldValue(customer?.emergencyContact, customer?.emergencyContactNumber, customer?.emergencyPhone)
    });
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!onUpdate) {
      setError('Update functionality not available');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Prepare data for API (only send the fields that API expects)
      const updateData = {
        name: editData.name !== 'N/A' ? editData.name : '',
        email: editData.email !== 'N/A' ? editData.email : '',
        gender: editData.gender !== 'N/A' ? editData.gender : '',
        address: editData.address !== 'N/A' ? editData.address : ''
      };

      await onUpdate(updateData);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error updating customer:', err);
      setError(err.message || 'Failed to update customer details');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: string) => (event: any) => {
    setEditData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Helper function to render field pair
  const renderFieldPair = (label: string, value: string) => {
    if (value === 'N/A') return null;

    return (
      <Box>
        <Typography variant="body2" sx={{ color: '#9E9E9E', fontSize: '0.875rem', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 400 }}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <DetailSection>
      <Box sx={sectionBoxSx}>
        {/* Header */}
        <Box sx={headerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={avatarSx}>
              <Typography sx={avatarTextSx}>
                C
              </Typography>
            </Box>
            <Typography variant="h6" sx={headerTitleSx}>
              CUSTOMER DETAILS
            </Typography>
          </Box>
          
          {/* Action Buttons - Only show MODIFY button in header */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isEditing && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                disabled={loading || !onUpdate || disabled}
                sx={modifyButtonSx}
              >
                MODIFY
              </Button>
            )}
          </Box>
        </Box>

        {/* Content */}
        <Box sx={contentBoxSx}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!isEditing ? (
            <Box sx={gridSx}>
              {/* Always show basic fields */}
              <Box>
                <Typography variant="body2" sx={labelSx}>
                  Customer Name
                </Typography>
                <Typography variant="body1" sx={valueSx}>
                  {getFieldValue(customer?.name, customer?.customerName, customer?.fullName)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={labelSx}>
                  Mobile Number
                </Typography>
                <Typography variant="body1" sx={valueSx}>
                  {getFieldValue(customer?.mobileNumber, customer?.mobile, customer?.phone, customer?.contactNumber)}
                </Typography>
              </Box>

              {/* Conditionally render other fields */}
              {renderFieldPair("Email", getFieldValue(customer?.email, customer?.emailId, customer?.emailAddress))}
              {renderFieldPair("Address", getFieldValue(customer?.address, customer?.customerAddress, customer?.fullAddress))} 
              {renderFieldPair("Gender", getFieldValue(customer?.gender))}    
              {renderFieldPair("Emergency Contact Name", getFieldValue(customer?.emergencyContactName, customer?.emergencyContactPerson, customer?.emergencyContactPersonName))}     
              {renderFieldPair("Emergency Contact Number", getFieldValue(customer?.emergencyContact, customer?.emergencyContactNumber, customer?.emergencyPhone))}
            </Box>
          ) : (
            <Box sx={gridSx}>
              <TextField
                fullWidth
                label="Customer Name"
                value={editData.name}
                onChange={handleFieldChange('name')}
                disabled={saving || disabled}
                size="small"
                sx={textFieldSx}
              />

              <Box>
                <Typography variant="body2" sx={labelSx}>
                  Mobile Number
                </Typography>
                <Typography variant="body1" sx={valueSx}>
                  {getFieldValue(customer?.mobileNumber, customer?.mobile, customer?.phone, customer?.contactNumber)}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Email"
                value={editData.email}
                onChange={handleFieldChange('email')}
                disabled={saving || disabled}
                size="small"
                sx={textFieldSx}
              />

              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Gender</InputLabel>
                <Select
                  value={editData.gender}
                  label="Gender"
                  onChange={handleFieldChange('gender')}
                  disabled={saving || disabled}
                  sx={selectSx}
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Address"
                value={editData.address}
                onChange={handleFieldChange('address')}
                disabled={saving || disabled}
                multiline
                rows={2}
                size="small"
                sx={addressFieldSx}
              />
            </Box>
          )}

          {/* Save/Cancel buttons at bottom when editing */}
          {isEditing && (
            <Box sx={editActionsSx}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={saving || disabled}
                sx={cancelButtonSx}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || disabled}
                sx={saveButtonSx}
              >
                {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </DetailSection>
  );
};

export default CustomerDetailsSection;
